import { observer } from "mobx-react"; // Import observer from mobx-react for state management
import moment from "moment"; // Import moment for date and time manipulation
import React, { useMemo } from "react"; // Import React library
import { FormattedMessage } from "react-intl"; // Import FormattedMessage for i18n
import demoStoreIcon from "../../../../assets/Images/man.png"; // Import demo store icon
import {
  formatTimeString, // Import formatTimeString utility
  getOverlappingBreaksByTime, // Import getOverlappingBreaksByTime utility
  getOverlappingEventsByTime, // Import getOverlappingEventsByTime utility
  isCurrentDateTimeGreater,
  IsStoreClosed, // Import isDayClosed utility
  isTimeOuterThanShiftColourModeUsingMinMaxTimeEffort,
  isTimeWithinShift,
} from "../../../../constants/constants"; // Import utility functions
import bookingStatusEnum from "../../../../constants/enums/booking-status-enum"; // Import booking status enum
import ReservationDetailsPopupHOC from "../hoc/reservation-details-popup-hoc"; // Import HOC for reservation details popup
import BreakEventComponent from "./break-event-component"; // Import BreakEventComponent
import CompletedEventComponent from "./completed-event-component"; // Import CompletedEventComponent
import EmptyCell from "./empty-cell-component"; // Import EmptyCell component
import Hours from "./hour-component"; // Import Hours component
import InProgressEvent from "./inprogress-event-component"; // Import InProgressEvent component
import Minutes from "./minutes-component"; // Import Minutes component
import ReceptionEventComponent from "./reception-event-component"; // Import ReceptionEventComponent
// import MoreEvents from "./more-events-component"; // Commented out import for more events component

/**
 * Props interface for the DailySchedulerComponent.
 *
 * @property {any[]} minutes - Array of minute intervals to display.
 * @property {any} hoursShown - The hours to be displayed in the scheduler.
 * @property {any} hrs - The hour data for the scheduler.
 * @property {number} minuteInterval - Interval between minutes in the scheduler.
 * @property {any[]} staffList - List of staff members available for scheduling.
 * @property {string} currentDate - The currently selected date in the scheduler.
 * @property {any[]} matchedEvents - List of events matched to the current schedule.
 * @property {() => void} openReservationDetails - Function to open reservation details.
 * @property {number} DEFAULT_TOP_MARGIN - Default top margin for the scheduler layout.
 * @property {number} TOTAL_ALLOWED_EVENTS_IN_ROW - Maximum number of events allowed in a single row.
 * @property {number} CELL_WIDTH - Width of each cell in the scheduler grid.
 * @property {string} view - The type of view (e.g., daily, weekly).
 * @property {any[]} getAllStaffOptions - Options for all staff members.
 * @property {() => void} openReservationWindow - Function to open the reservation window.
 * @property {any[]} getStaffAppointments - Data for staff appointments.
 * @property {any[]} getBreaks - Data for breaks in the schedule.
 * @property {string[]} minutesTd - Array of minute intervals for the table.
 * @property {string | number} selectedStaff - ID of the selected staff member.
 * @property {string} openTime - The opening time for the scheduler.
 * @property {any[]} droppedEvents - Data for events that have been dropped.
 * @property {(id: string | number, name: string) => void} handleStaffSelect - Function to handle staff selection.
 */

interface IProps {
  minutes: any[]; // Array of minute intervals
  hoursShown: any; // Hours to be displayed
  hours: any[]; // Hour data
  minuteInterval: number; // Interval between minutes
  DEFAULT_TOP_MARGIN: number; // Default top margin for layout
  TOTAL_ALLOWED_EVENTS_IN_ROW: number; // Max events in a row
  CELL_WIDTH: number; // Width of each cell
  currentDate?: string; // Currently selected date
  view?: string; // View type (e.g., daily)
  getAllStaffOptions?: any[]; // Options for all staff
  handleStaffSelect: (id: string | number, name: string) => void; // Function to handle staff selection
  selectedStaff?: string | number; // Selected staff ID
  openTime?: string; // Opening time
  minutesTd: string[]; // Array of minute intervals for table
  getStaffAppointments: any[]; // Staff appointments data
  getBreaks: any[]; // Breaks data
  openReservationWindow: (params: any) => void; // Function to open reservation window
  droppedEvents?: any[]; // Dropped events data
  days: any; // Days data
  hourColumnWidthPx: any; // Hour column width in pixels
  minuteColumnWidthPx: any; // Minute column width in pixels
  tableRef: any; // Reference to the table element
  getReservationSettingDetail: any; // Reservation setting details
  storeId: number; // Pass the store ID
  viewOnly: boolean; // Pass the view only flag or read only
  shiftScheduleList: any[]; // Shift schedule list data
  reservationSettingMinMaxDetail: any; // Reservation setting min-max details
  dailyBusinessMinMaxTime?: any; // Daily business min-max time
}

const DailySchedulerComponent = (props: IProps) => {
  // DailySchedulerComponent functional component
  const {
    minuteInterval, // Minute interval
    minutes, // Minutes array
    hours, // Hour data
    hoursShown, // Hours to be displayed
    view, // View type
    getAllStaffOptions, // All staff options
    getBreaks, // Breaks data
    droppedEvents, // Dropped events
    minutesTd, // Minute intervals for table
    openTime, // Opening time
    selectedStaff, // Selected staff ID
    getStaffAppointments, // Staff appointments data
    CELL_WIDTH, // Cell width
    DEFAULT_TOP_MARGIN, // Default top margin
    openReservationWindow, // Function to open reservation window
    currentDate, // Currently selected date
    handleStaffSelect, // Function to handle staff selection
    days, // Days data
    hourColumnWidthPx, // Hour column width in pixels
    minuteColumnWidthPx, // Minute column width in pixels
    tableRef, // Reference to the table element
    getReservationSettingDetail, // Reservation setting details
    storeId, // Store ID
    viewOnly, // View only flag
    reservationSettingMinMaxDetail, // Reservation setting min-max details
    dailyBusinessMinMaxTime, // Daily business min-max time
  } = props;

  const totalhours = hours.length; // Total number of hours

  const formatDate = (dateStr: string): string =>
    dateStr?.split("T")[0] || dateStr;

  const calculateEventsOrBreak = (params: any) => {
    const allMatchedEvents = getOverlappingEventsByTime(
      getStaffAppointments,
      params
    );
    const matchedBreaks = getOverlappingBreaksByTime(getBreaks, params);

    let visibleEvents: any[] = [];
    let visibleBreaksCount = 0;
    let breakConflictCount = 0;

    for (const e of allMatchedEvents) {
      if (e.IsShow) visibleEvents.push(e);
      if (e.HasBreakConflict) breakConflictCount++;
    }

    for (const b of matchedBreaks) {
      if (b.IsShow) visibleBreaksCount++;
    }

    return {
      MatchedEvents: allMatchedEvents,
      MatchedBreaks: matchedBreaks,
      VisibleEvents: visibleEvents,
      TBRC: breakConflictCount,
      TRC: visibleEvents.length,
      TRCB: visibleBreaksCount,
    };
  };

  const calculateRows = (data: any) => {
    const count = data.TRC + data.TBRC;
    return count >= 2 ? 2 : Math.max(1, count);
  };

  const calculateIsOverlappingSameTime = (
    matchedEvents: any[],
    matchedBreaks: any[]
  ) => ({
    IsOverlappingSameTimeEvents:
      matchedEvents?.length === 2 &&
      matchedEvents[0]?.StartTime === matchedEvents[1]?.StartTime,
    IsOverlappingSameTimeBreak:
      matchedBreaks?.length === 2 &&
      matchedBreaks[0]?.StartTime === matchedBreaks[1]?.StartTime,
  });

  const calculateCellDisabled = (
    viewOnly: boolean,
    day: any,
    timeLabel: string
  ): boolean => {
    if (viewOnly) return true;
    const dateIndex = day?.props?.["data-index"];
    if (!dateIndex) return false;
    // return isCurrentDateTimeGreater(formatDate(dateIndex), timeLabel);
    return viewOnly;
  };

  const getCellBackgroundClass = (
    viewOnly: boolean,
    day: any,
    timeLabel: string,
    props: any,
    shift: any,
    shiftMapCache: Map<number, boolean>,
    dateCache: Map<string, string>
  ): string => {
    const dateStr = day?.props?.["data-index"];
    if (!dateStr) return "bg-shift-not-assigned";

    const formattedDate = dateCache?.get(dateStr) ?? formatDate(dateStr);
    if (dateCache && !dateCache.has(dateStr))
      dateCache.set(dateStr, formattedDate);

    if (viewOnly || isCurrentDateTimeGreater(formattedDate, timeLabel))
      return "bg-gray";

    const staffId = day?.props?.["data-action"];
    const hasShift =
      shiftMapCache?.get(staffId) ??
      props.shiftScheduleList?.some((item: any) => item.StaffId === staffId);
    if (shiftMapCache && !shiftMapCache.has(staffId))
      shiftMapCache.set(staffId, !!hasShift);

    if (!hasShift) return "bg-shift-not-assigned";
    if (isTimeWithinShift(shift, timeLabel)) return "bg-inside-shift";

    if (
      isTimeOuterThanShiftColourModeUsingMinMaxTimeEffort(
        props.dailyBusinessMinMaxTime,
        timeLabel,
        formattedDate
      )
    )
      return "bg-outside-shift";

    return "bg-inside-business-hours";
  };

  const computeEventDetails = (
  getStaffAppointments: any[],
  getBreaks: any[],
  matchedId: any,
  minuteInterval: number,
  matchedEvents: any[],
  droppedEvents?: any[]
) => {
  const finalObject = getStaffAppointments?.find((item) => item?.Id === matchedId?.Id);
  if (!finalObject) return null;

  const allOverlappingBreaks = finalObject?.OverlappedBreaks?.map((b: any) =>
    getBreaks?.find((br: any) => br?.BreakID === b?.BreakID)
  );

  const totalColspan = Math.ceil(Number(finalObject?.TotalTime) / minuteInterval);
  const startEnd = Array.from({ length: totalColspan }, (_, i) => i + 1);
  const start = startEnd[0];
  const end = startEnd[startEnd.length - 1];
  const overlappingCount = (matchedEvents?.length ?? 1) - 1;
  const isDropped = droppedEvents?.find((item) => item.Id === finalObject?.Id);
  const eventTRBC = finalObject?.OverlappedBreaks?.length;

  return {
    "FinalObject" : finalObject,
    "AllOverlappingBreaks" : allOverlappingBreaks,
    "TotalColspan" : totalColspan,
    "EventStartCell" : start,
    "EventEndCell" : end,
    "EventOverlappingCount" : overlappingCount,
    "IsEventDropped" : isDropped,
    "EventTRBC" : eventTRBC
  };
};


  return (
    <React.Fragment>
      {" "}
      {/* Fragment wrapper */}
      <div
        className="scheduler-table daily-view" // Scheduler table class
        ref={tableRef} // Table ref
        style={{ width: "100%", minWidth: "900px" }} // Table style
      >
        {/* Table header with staff names and time slots */}
        {IsStoreClosed(getReservationSettingDetail) == false ? ( // If day is not closed
          <table
            className="table table-bordered" // Table class
            // Set table width and minimum width
          >
            <thead>
              <tr>
                <th className="day-column" rowSpan={2}>
                  {" "}
                  {/* Staff column header */}
                  <FormattedMessage id="staff" /> {/* Staff label */}
                </th>
                <Hours
                  hours={hours} // Pass hours data
                  hoursShown={totalhours} // Pass hours to be displayed
                  minuteInterval={minuteInterval} // Pass minute interval
                  hourColumnWidthPx={hourColumnWidthPx} // Pass hour column width
                />
              </tr>
              <tr>
                <Minutes
                  minutes={minutes} // Pass minutes array
                  hoursShown={totalhours} // Pass hours to be displayed
                  minuteColumnWidthPx={minuteColumnWidthPx} // Pass minute column width
                />
              </tr>
            </thead>
            <tbody>
              {days.map(
                (
                  day: any,
                  d: any // Map over days
                ) => (
                  <tr key={d}>
                    {" "}
                    {/* Row for each day */}
                    {view !== "daily" && // Check if the view is not daily
                      d === 0 && ( // Check if it's the first row
                        <td className="day-column align-top" rowSpan={7}>
                          {" "}
                          {/* Day column with rowspan for staff */}{" "}
                          {/* Staff column */}
                          <div className="flex flex-col items-center space-y-3">
                            {" "}
                            {/* Container for staff list */}{" "}
                            {/* Staff list container */}
                            {getAllStaffOptions?.map(
                              // Map over staff options
                              (
                                staff // Current staff item
                              ) => (
                                <a
                                  key={staff.id} // Unique key for staff
                                  onClick={() =>
                                    handleStaffSelect(
                                      // Handle staff selection on click
                                      staff.id,
                                      staff?.data?.StaffName // Pass staff ID and name
                                    )
                                  }
                                  className={`flex items-center ${
                                    selectedStaff === staff.id ? "selected" : "" // Highlight selected staff
                                  }`}
                                >
                                  <img
                                    src={staff.data?.Image ?? demoStoreIcon} // Staff image or default icon
                                    alt={staff.data?.StaffName} // Alt text for staff name
                                    className="staffImg" // Class for staff image
                                  />
                                </a>
                              )
                            )}
                          </div>
                        </td>
                      )}
                    <td className="day-column">{day}</td> {/* Day column */}
                    {/* Render the time slots for each hour and minute interval */}
                    {[...Array(totalhours)].map(
                      (
                        _,
                        h // Loop over hours
                      ) =>
                        minutesTd.map((m, i) => {
                          // Loop over minutes
                          const hour = moment(openTime, "HH:mm").hour() + h; // Calculate base hour from open time
                          // const hour = baseHour + h; // Calculate current hour by adding the loop index
                          const timeLabel = formatTimeString(`${hour}${m}`); // Format time string for display
                          const params = {
                            date: localStorage.getItem("currentDate"), // Get current date from localStorage
                            time: timeLabel, // Current time label for the appointment
                            staffId: Number(day.props["data-action"]), // Staff ID from the day's props
                          };
                          
                          let calculatedData = calculateEventsOrBreak(params);
                          let allMatchedEvents = calculatedData.MatchedEvents;
                          let matchedBreaks = calculatedData.MatchedBreaks; // Store matched breaks for further processing
                          let matchedEvents = calculatedData.VisibleEvents;
                          let calculatedRows = calculateRows(calculatedData); // Calculate the number of rows needed for rendering
                          let isOverlapping = calculateIsOverlappingSameTime(matchedEvents, matchedBreaks);
                          
                          let shift = props.shiftScheduleList?.find(
                            (item: any) =>
                              item?.StaffId == day.props["data-action"] // Find the shift for the current staff
                          );
                          
                          const dateCache = new Map<string, string>();
                          const shiftMapCache = new Map<number, boolean>();
                          const bgClass = getCellBackgroundClass(viewOnly, day, timeLabel, props, shift, shiftMapCache, dateCache);
                          const isDisabled = calculateCellDisabled(viewOnly, day, timeLabel);

                          if (matchedEvents?.length > 0) {
                            // If there are matched events
                            return (
                              <td
                                key={`MATCHED-${day.props["data-index"]}-${timeLabel}-IN-${i}`} // Unique key for cell
                                className={`minute-cell position-relative 
                                ${bgClass}
                                `}
                                // Set cell width and height based on rows and default margin
                                style={{
                                  width: CELL_WIDTH, // Set the width of the cell
                                  height: calculatedRows * DEFAULT_TOP_MARGIN, // Set the height based on the number of rows and default margin
                                }}
                                // Data label for debugging or testing
                                // Unique data label for the cell
                              >
                                {matchedEvents.map(
                                  // Map over matched events
                                  (matchedId: any, index: number) => {

                                    const calculateEventBreakDetails = computeEventDetails(
                                      getStaffAppointments,
                                      getBreaks,
                                      matchedId,
                                      minuteInterval,
                                      matchedEvents,
                                      droppedEvents
                                    );

                                    // If the event starts at this time
                                    if (
                                      calculateEventBreakDetails?.FinalObject?.StartTime == timeLabel && // Check if event starts at the current time label
                                      calculateEventBreakDetails?.IsEventDropped == undefined // Ensure the event is not dropped
                                    ) {
                                      if (
                                        calculateEventBreakDetails?.FinalObject?.IsShow == true &&
                                        calculateEventBreakDetails?.IsEventDropped == undefined // Check visibility and drop status
                                      ) {
                                        return (
                                            <React.Fragment key={`RF-RES-${day.props["data-index"]}-${timeLabel}-IN-${index}`}>
                                              {(() => {
                                                // Determine which component to render
                                                let EventComponent = InProgressEvent;

                                                if (calculateEventBreakDetails?.FinalObject?.AppointmentStatus === bookingStatusEnum.Complete) {
                                                  EventComponent = CompletedEventComponent;
                                                } else if (calculateEventBreakDetails?.FinalObject?.AppointmentStatus === bookingStatusEnum.InProcess) {
                                                  EventComponent = ReceptionEventComponent;
                                                }

                                                // Common props for all event components
                                                const eventProps = {
                                                  day,
                                                  currentDate: moment(currentDate).format("YYYY-MM-DD"),
                                                  dataObject: calculateEventBreakDetails?.FinalObject,
                                                  timeLabel,
                                                  start: calculateEventBreakDetails?.EventStartCell,
                                                  end: calculateEventBreakDetails?.EventEndCell,
                                                  staffList: props.getAllStaffOptions,
                                                  overlappingCount:
                                                    isOverlapping.IsOverlappingSameTimeEvents ? index : calculateEventBreakDetails?.EventOverlappingCount,
                                                  allEvents: getStaffAppointments,
                                                  allBreaks: getBreaks,
                                                  defaultMargin: DEFAULT_TOP_MARGIN,
                                                  allMatchedBreaks: calculateEventBreakDetails?.AllOverlappingBreaks,
                                                  breaksCount: calculateEventBreakDetails?.EventTRBC,
                                                  events: allMatchedEvents,
                                                  cellWidth: CELL_WIDTH,
                                                  storeId,
                                                  viewOnly,
                                                };

                                                return (
                                                  <EventComponent
                                                    key={`RES-${day.props["data-index"]}-${timeLabel}-IN-${index}`}
                                                    {...eventProps}
                                                    index={index} // only InProgressEvent needs this, extra prop doesn't break others
                                                  />
                                                );
                                              })()}

                                              {/* CASE 1: Overlapping events → Insert space cell only for index 0 */}
                                              {index === 0 && isOverlapping.IsOverlappingSameTimeEvents && (
                                                <EmptyCell
                                                  key={`RES-EMPTY-${day.props["data-index"]}-${timeLabel}-IN-${index}`}
                                                  staffId={day.props["data-action"]}
                                                  staffName={day.props["data-id"]}
                                                  timeLabel={timeLabel}
                                                  disabled={isDisabled}
                                                  day={day}
                                                  staffList={getAllStaffOptions}
                                                  openWindow={openReservationWindow}
                                                  storeId={storeId}
                                                  type="o1"
                                                />
                                              )}

                                              {/* CASE 2: Only one event and not overlapping */}
                                              {index === 0 && !isOverlapping.IsOverlappingSameTimeEvents && matchedEvents?.length === 1 && (
                                                <EmptyCell
                                                  key={`RES-EMPTY-${day.props["data-index"]}-${timeLabel}-IN-${index}`}
                                                  staffId={day.props["data-action"]}
                                                  staffName={day.props["data-id"]}
                                                  timeLabel={timeLabel}
                                                  disabled={isDisabled}
                                                  day={day}
                                                  staffList={getAllStaffOptions}
                                                  openWindow={openReservationWindow}
                                                  storeId={storeId}
                                                  type="o2"
                                                />
                                              )}
                                            </React.Fragment>
                                          );

                                      } else {
                                        return (
                                          <React.Fragment
                                            key={`RF-EMP-RES-${day.props["data-index"]}-${timeLabel}-IN-${index}`}
                                          >
                                            {/* Render an EmptyCell component for the current time slot */}
                                            <EmptyCell
                                              key={`RES-EMPTY-${day.props["data-index"]}-${timeLabel}-IN-${index}`} // Unique key for the EmptyCell
                                              staffId={day.props["data-action"]} // Staff ID associated with the cell
                                              staffName={day.props["data-id"]} // Name of the staff member
                                              timeLabel={timeLabel} // Time label for the current cell
                                              disabled={calculateCellDisabled(
                                                viewOnly,
                                                day,
                                                timeLabel
                                              )}
                                              day={day} // Pass the day data to the EmptyCell
                                              staffList={getAllStaffOptions} // Pass the list of all staff options
                                              openWindow={openReservationWindow} // Function to open the reservation window
                                              storeId={storeId} // Store ID for the current context
                                              type="o3" // Type identifier for the EmptyCell
                                            />
                                          </React.Fragment>
                                        );
                                      }
                                    } else {
                                      return (
                                        <React.Fragment
                                          key={`RF-EMP-RES-${day.props["data-index"]}-${timeLabel}-IN-${index}`}
                                        >
                                          {index == 0 && ( // Check if this is the first index
                                            <EmptyCell
                                              key={`RES-EMPTY-${day.props["data-index"]}-${timeLabel}-IN-${index}`} // Unique key for the EmptyCell
                                              staffId={day.props["data-action"]} // Staff ID associated with the cell
                                              staffName={day.props["data-id"]} // Name of the staff member
                                              timeLabel={timeLabel} // Time label for the current cell
                                              day={day} // Pass day data to the EmptyCell
                                              disabled={calculateCellDisabled(
                                                viewOnly,
                                                day,
                                                timeLabel
                                              )}
                                              staffList={getAllStaffOptions} // Pass the list of all staff options
                                              openWindow={openReservationWindow} // Function to open the reservation window
                                              storeId={storeId} // Store ID for the current context
                                              type="o4" // Type identifier for the EmptyCell
                                            />
                                          )}
                                        </React.Fragment>
                                      );
                                    }
                                  }
                                )}
                                {/* Render overlapping breaks if less than 2 events */}
                                {calculatedData.TRC < 2 &&
                                  matchedEvents?.map(
                                    (event: any, index: number) => {
                                      // Number of overlapped breaks for this event
                                      let trbc =
                                        event?.OverlappedBreaks?.length; // Get the count of overlapping breaks for the current event
                                      // Map over each overlapped break
                                      return event?.OverlappedBreaks?.map(
                                        (breakItem: any) => {
                                          // Find the break object in the breaks list using BreakID
                                          var finalObject = getBreaks?.find(
                                            (item) =>
                                              item?.BreakID ==
                                              breakItem?.BreakID
                                          );

                                          // Calculate colspan for the break based on its total time
                                          let totalColspan = Math.ceil(
                                            Number(finalObject?.TotalTime) /
                                              minuteInterval
                                          );
                                          // Create an array for start and end indices based on colspan
                                          let startEnd = Array.from(
                                            { length: totalColspan },
                                            (_, i) => i + 1
                                          );
                                          // Start index for the break
                                          let start = startEnd[0];
                                          // End index for the break
                                          let end =
                                            startEnd[startEnd?.length - 1];
                                          // Overlapping count for breaks, set to 1 for this case
                                          const overlappingCount = 1;

                                          // If break starts at this time and is visible
                                          if (
                                            finalObject?.StartTime == timeLabel
                                          ) {
                                            if (breakItem?.IsShow == true) {
                                              // Check if the break item is set to be shown
                                              return (
                                                // Return the following JSX if the condition is met
                                                <React.Fragment
                                                  key={`RF-BR-${day.props["data-index"]}-${timeLabel}-IN-${index}`}
                                                >
                                                  {" "}
                                                  {/* Unique key for the fragment based on day index and time label */}
                                                  <BreakEventComponent
                                                    day={day} // Pass the day object to the BreakEventComponent
                                                    currentDate={moment(
                                                      // Format and pass the current date
                                                      currentDate
                                                    ).format("YYYY-MM-DD")}
                                                    dataObject={finalObject} // Pass the final break object
                                                    timeLabel={timeLabel} // Pass the time label for the break
                                                    start={start} // Pass the start index for the break
                                                    end={end} // Pass the end index for the break
                                                    overlappingCount={
                                                      // Pass the count of overlapping breaks
                                                      overlappingCount
                                                    }
                                                    staffList={
                                                      // Pass the list of all staff options
                                                      getAllStaffOptions
                                                    }
                                                    defaultMargin={
                                                      // Pass the default margin for layout
                                                      DEFAULT_TOP_MARGIN
                                                    }
                                                    allMatchedEvents={
                                                      // Pass all matched events for the break
                                                      matchedBreaks
                                                    }
                                                    cellWidth={CELL_WIDTH} // Pass the width of the cell
                                                    storeId={storeId} // Pass the store ID for context
                                                    viewOnly={viewOnly} // Pass the view-only flag
                                                    // breaksCount is number of breaks minus 1
                                                    breaksCount={trbc} // Pass the count of overlapping breaks
                                                    // Show "All More Events" popup if total events and breaks > 2
                                                    showAllMoreEventsPopup={
                                                      // Control visibility of the popup
                                                      false
                                                    }
                                                  />
                                                </React.Fragment>
                                              ); // End of the return statement
                                            }
                                          }
                                        }
                                      );
                                    }
                                  )}
                              </td>
                            );
                          } else if (matchedBreaks?.length > 0) {
                            // If there are matched breaks
                            return (
                              <td
                                key={`BR-${day.props["data-index"]}-${timeLabel}-IN-${i}`} // Unique key for cell based on day index and time label
                                className={`minute-cell position-relative                                                       
                                ${bgClass}
                              `}
                                style={{
                                  width: CELL_WIDTH, // Set cell width
                                  height:
                                    calculatedData.TRCB *
                                    DEFAULT_TOP_MARGIN, // Set cell height based on the number of rendered breaks and default margin
                                }}
                              >
                                {matchedBreaks?.map(
                                  // Map over matched breaks
                                  (matchedId: any, index: number) => {
                                    // Map over matched breaks
                                    var finalObject = getBreaks?.find(
                                      (item) =>
                                        item?.BreakID == matchedId?.BreakID // Find matching break by BreakID
                                    );

                                    let totalColspan = Math.ceil(
                                      Number(finalObject?.TotalTime) /
                                        minuteInterval // Calculate colspan based on total time divided by minute interval
                                    );
                                    let startEnd = Array.from(
                                      { length: totalColspan }, // Generate an array with a length equal to totalColspan
                                      (_, i) => i + 1 // Fill the array with values from 1 to totalColspan
                                    );
                                    let start = startEnd[0]; // Get the start index (first element of the array)
                                    let end = startEnd[startEnd?.length - 1]; // Get the end index (last element of the array)
                                    const overlappingCount =
                                      matchedBreaks?.length - 1; // Count overlapping breaks by subtracting 1 from the length of matchedBreaks

                                    if (finalObject?.StartTime == timeLabel) {
                                      // If break starts at this time
                                      if (finalObject?.IsShow == true) {
                                        // If break is visible
                                        return (
                                          // Start of return statement for the BreakEventComponent
                                          <React.Fragment
                                            key={`RF-BR-${day.props["data-index"]}-${timeLabel}-IN-${index}`}
                                          >
                                            {" "}
                                            {/* Unique key for the fragment */}
                                            <BreakEventComponent
                                              day={day} // Pass day data
                                              currentDate={moment(
                                                // Format current date using moment.js
                                                currentDate
                                              ).format("YYYY-MM-DD")} // Format current date to YYYY-MM-DD
                                              dataObject={finalObject} // Pass break data
                                              timeLabel={timeLabel} // Pass time label
                                              start={start} // Pass start index
                                              end={end} // Pass end index
                                              overlappingCount={
                                                // Pass count of overlapping breaks
                                                overlappingCount
                                              }
                                              staffList={getAllStaffOptions} // Pass staff list
                                              defaultMargin={DEFAULT_TOP_MARGIN} // Pass default margin for layout
                                              allMatchedEvents={matchedBreaks} // Pass matched breaks
                                              cellWidth={CELL_WIDTH} // Pass cell width for layout
                                              storeId={storeId} // Pass the store ID
                                              viewOnly={viewOnly} // Pass view only flag
                                            />
                                            {index == 0 && ( // Check if this is the first index
                                              <React.Fragment
                                                key={`RF-BR-EMPTY-${day.props["data-index"]}-${timeLabel}-IN-${index}`}
                                              >
                                                {" "}
                                                {/* Unique key for the empty cell fragment */}
                                                <EmptyCell
                                                  key={`BR-EMPTY-${day.props["data-index"]}-${timeLabel}-IN-${index}`} // Unique key for the EmptyCell
                                                  staffId={
                                                    // Staff ID for the empty cell
                                                    day.props["data-action"]
                                                  }
                                                  staffName={
                                                    // Pass staff name for the empty cell
                                                    day.props["data-id"]
                                                  }
                                                  timeLabel={timeLabel} // Pass time label for the empty cell
                                                  disabled={isDisabled}
                                                  day={day} // Pass day data to the EmptyCell
                                                  staffList={getAllStaffOptions} // Pass staff list to the EmptyCell
                                                  openWindow={
                                                    // Function to open the reservation window
                                                    openReservationWindow
                                                  }
                                                  storeId={storeId} // Pass the store ID
                                                />
                                              </React.Fragment>
                                            )}
                                          </React.Fragment>
                                        ); // End of return statement for the BreakEventComponent
                                      } else {
                                        return (
                                          <React.Fragment
                                            key={`RF-BR-EMPTY-${day.props["data-index"]}-${timeLabel}-IN-${index}`}
                                          >
                                            {" "}
                                            {/* Unique key for the fragment */}
                                            <EmptyCell
                                              key={`BR-EMPTY-${day.props["data-index"]}-${timeLabel}-IN-${index}`} // Unique key for the EmptyCell
                                              staffId={day.props["data-action"]} // Staff ID associated with the cell
                                              staffName={day.props["data-id"]} // Name of the staff member
                                              timeLabel={timeLabel} // Time label for the current cell
                                              disabled={isDisabled}
                                              day={day} // Pass day data to the EmptyCell
                                              staffList={getAllStaffOptions} // Pass the list of all staff options
                                              openWindow={openReservationWindow} // Function to open the reservation window
                                              storeId={storeId} // Store ID for the current context
                                            />
                                          </React.Fragment>
                                        );
                                      }
                                    } else {
                                      return (
                                        <React.Fragment
                                          key={`RF-BR-EMPTY-${day.props["data-index"]}-${timeLabel}-IN-${index}`}
                                        >
                                          {" "}
                                          {/* Unique key for the fragment */}
                                          <EmptyCell
                                            key={`BR-EMPTY-${day.props["data-index"]}-${timeLabel}-IN-${index}`} // Unique key for the EmptyCell
                                            staffId={day.props["data-action"]} // Staff ID associated with the cell
                                            staffName={day.props["data-id"]} // Name of the staff member
                                            timeLabel={timeLabel} // Time label for the current cell
                                            disabled={isDisabled}
                                            day={day} // Pass day data to the EmptyCell
                                            staffList={getAllStaffOptions} // Pass the list of all staff options
                                            openWindow={openReservationWindow} // Function to open the reservation window
                                            storeId={storeId} // Store ID for the current context
                                          />
                                        </React.Fragment>
                                      );
                                    }
                                  }
                                )}
                              </td>
                            );
                          } else {
                            // If no events or breaks
                            return (
                              <td
                                // Unique key for cell
                                className={`minute-cell position-relative                               
                                ${bgClass}
                                `}
                                // Set display style to table-cell
                                style={{
                                  display: "table-cell", // Ensure the cell is displayed as a table cell
                                  width: CELL_WIDTH, // Set cell width
                                }}
                              >
                                {/* Draggable event component */}
                                <EmptyCell
                                  key={`EMPTY-${day.props["data-index"]}-${timeLabel}}`} // Unique key for the EmptyCell
                                  staffId={day.props["data-action"]} // Staff ID
                                  staffName={day.props["data-id"]} // Pass staff name
                                  timeLabel={timeLabel} // Pass time label
                                  day={day} // Pass day data
                                  disabled={isDisabled}
                                  staffList={getAllStaffOptions} // Pass staff list
                                  openWindow={openReservationWindow} // Pass open window function
                                  storeId={storeId} // Pass the store ID
                                />
                              </td>
                            );
                          }
                        })
                    )}
                  </tr>
                )
              )}
            </tbody>
          </table>
        ) : (
          // If day is closed
          <div className="text-center store-closed-message">
            {" "}
            {/* Store closed message */}
            <svg
              clipRule="evenodd" // Set the clip rule for SVG
              fillRule="evenodd" // Set the fill rule for SVG
              stroke-linejoin="round" // Set the line join style for strokes
              stroke-miterlimit="2" // Set the miter limit for strokes
              viewBox="0 0 32 32" // Define the view box for the SVG
              xmlns="http://www.w3.org/2000/svg" // Set the XML namespace for SVG
              id="fi_11738549" // Set an ID for the SVG
            >
              <g transform="translate(-259)">
                {" "}
                {/* Group for translation */}
                <g transform="translate(222)">
                  {" "}
                  {/* Nested group for translation */}
                  <path
                    d="m53 1c-8.279 0-15 6.721-15 15s6.721 15 15 15 15-6.721 15-15-6.721-15-15-15zm0 2c7.175 0 13 5.825 13 13s-5.825 13-13 13-13-5.825-13-13 5.825-13 13-13z" // Define the path for the circle
                    fill="#e81c1c" // Set the fill color for the circle
                  ></path>
                </g>
                <path
                  d="m273.586 16-4.95 4.95c-.39.39-.39 1.024 0 1.414s1.024.39 1.414 0l4.95-4.95 4.95 4.95c.39.39 1.024.39 1.414 0s.39-1.024 0-1.414l-4.95-4.95 4.95-4.95c.39-.39.39-1.024 0-1.414s-1.024-.39-1.414 0l-4.95 4.95-4.95-4.95c-.39-.39-1.024-.39-1.414 0s-.39 1.024 0 1.414z" // Define the path for the cross
                  fill="#7a0000" // Set the fill color for the cross
                ></path>
              </g>
            </svg>
            <p className="text-danger">
              <FormattedMessage id="store_is_closed" />{" "}
              {/* Store closed label */}
              {/* Display store closed message */}
            </p>{" "}
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default ReservationDetailsPopupHOC(observer(DailySchedulerComponent)); // Export component wrapped with HOC and observer
